/**
 * How every projectile looks, in one content file
 * (game-content/data/projectiles.json, #335): enemy attacks, ranged
 * weapons, Essence abilities and perk shots each name an entry by its id
 * (`projectileId`), and the host stamps that id on the projectile it sends.
 * Only looks live here; how a projectile flies (speed, size, reach, a lob's
 * arc) stays with the attack or weapon that throws it.
 */
import { FieldReader, toUnionValues, type Fields } from './content-fields';
import { getAllEssenceTypes, type EssenceType } from './types/essence';

/** What is seen in flight; the client picks the engine system that draws it. */
export type ProjectileAppearance = 'brimstone-streak' | 'brimstone-chunk' | 'hellfire' | 'arrow' | 'wrath-bolt' | 'thrown-weapon';

export const PROJECTILE_APPEARANCES: readonly ProjectileAppearance[] = toUnionValues<ProjectileAppearance>({
    'brimstone-streak': true,
    'brimstone-chunk': true,
    hellfire: true,
    arrow: true,
    'wrath-bolt': true,
    'thrown-weapon': true,
});

/**
 * How it leaves its thrower: `cast` gathers in the hand through the windup
 * and flies from it; `throw` sits in the hand in place of the weapon held
 * there from the wind-back until it lands.
 */
export type ProjectileRelease = 'cast' | 'throw';

const PROJECTILE_RELEASES: readonly ProjectileRelease[] = toUnionValues<ProjectileRelease>({ cast: true, throw: true });

/** Which hand a released projectile leaves from. */
export type ProjectileHand = 'main' | 'off';

const PROJECTILE_HANDS: readonly ProjectileHand[] = toUnionValues<ProjectileHand>({ main: true, off: true });

/** One of a model's own axes. */
export type ProjectileModelAxis = 'x' | 'y' | 'z';

const MODEL_AXES: readonly ProjectileModelAxis[] = toUnionValues<ProjectileModelAxis>({ x: true, y: true, z: true });

export interface ProjectileDefinition {
    readonly appearance: ProjectileAppearance;
    /** Absent: it just appears where it was launched (a ricochet off the enemy struck). */
    readonly release?: ProjectileRelease;
    /** Set exactly when `release` is. */
    readonly hand?: ProjectileHand;
    /** A thrown weapon's tether back to its thrower, in this essence type's color. */
    readonly tether?: EssenceType;
    /** A thrown weapon's model axis it tumbles about; by default its second-longest side. */
    readonly spinAxis?: ProjectileModelAxis;
    /** A thrown weapon's model axis from grip to head, so it lands head down; by default the longest of the other two. */
    readonly lengthAxis?: ProjectileModelAxis;
}

/** Every projectile's look, by its id. */
export type ProjectileCatalog = Readonly<Record<string, ProjectileDefinition>>;

export interface ProjectileCatalogParseResult {
    /** The well-formed entries; one with a problem is left out. */
    readonly catalog: ProjectileCatalog;
    /** What is wrong, one line each, led by the entry's id. */
    readonly problems: readonly string[];
}

const KNOWN_FIELDS: ReadonlySet<string> = new Set<keyof ProjectileDefinition>(['appearance', 'release', 'hand', 'tether', 'spinAxis', 'lengthAxis']);

const PROJECTILE_ID = /^[a-z0-9]+(-[a-z0-9]+)*$/;

/** Parses the catalog file: each entry checked on its own, so one bad entry never takes the others down. */
export function parseProjectileCatalog(value: unknown): ProjectileCatalogParseResult {
    const fileReader = new FieldReader();
    const entries = fileReader.fields(value, 'The projectile catalog');
    if (!entries) {
        return { catalog: {}, problems: fileReader.problems };
    }
    const catalog: Record<string, ProjectileDefinition> = {};
    const problems: string[] = [];
    for (const [id, entry] of Object.entries(entries)) {
        const reader = new FieldReader();
        if (!PROJECTILE_ID.test(id)) {
            reader.problems.push('Id must be lowercase letters and digits joined by single dashes.');
        }
        const definition = readDefinition(reader, entry);
        if (definition && reader.problems.length === 0) {
            catalog[id] = definition;
        }
        problems.push(...reader.problems.map((problem) => `${id}: ${problem}`));
    }
    return { catalog, problems };
}

function readDefinition(reader: FieldReader, entry: unknown): ProjectileDefinition | undefined {
    const fields = reader.fields(entry, 'The entry');
    if (!fields) {
        return undefined;
    }
    for (const key of Object.keys(fields)) {
        if (!KNOWN_FIELDS.has(key)) {
            reader.problems.push(`${key} is not a projectile field.`);
        }
    }
    const appearance = reader.oneOf(fields, 'appearance', 'Appearance', PROJECTILE_APPEARANCES);
    const release = optionalOneOf(reader, fields, 'release', 'Release', PROJECTILE_RELEASES);
    const hand = optionalOneOf(reader, fields, 'hand', 'Hand', PROJECTILE_HANDS);
    if (release !== undefined && hand === undefined && fields.hand === undefined) {
        reader.problems.push('Hand is required with a release.');
    }
    if (hand !== undefined && fields.release === undefined) {
        reader.problems.push('Hand is only for a projectile with a release.');
    }
    const tether = optionalOneOf(reader, fields, 'tether', 'Tether', getAllEssenceTypes());
    const spinAxis = optionalOneOf(reader, fields, 'spinAxis', 'Spin axis', MODEL_AXES);
    const lengthAxis = optionalOneOf(reader, fields, 'lengthAxis', 'Length axis', MODEL_AXES);
    if (appearance === 'thrown-weapon') {
        // The engine reels a thrown weapon back along its tether: one without a tether can't be drawn yet.
        if (fields.tether === undefined) {
            reader.problems.push('Tether is required for a thrown weapon.');
        }
    } else if (appearance !== undefined) {
        const onlyThrown: readonly [keyof ProjectileDefinition, string][] = [['tether', 'Tether'], ['spinAxis', 'Spin axis'], ['lengthAxis', 'Length axis']];
        for (const [key, label] of onlyThrown) {
            if (fields[key] !== undefined) {
                reader.problems.push(`${label} is only for a thrown weapon.`);
            }
        }
    }
    if (appearance === undefined) {
        return undefined;
    }
    return {
        appearance,
        ...(release !== undefined ? { release } : {}),
        ...(hand !== undefined ? { hand } : {}),
        ...(tether !== undefined ? { tether } : {}),
        ...(spinAxis !== undefined ? { spinAxis } : {}),
        ...(lengthAxis !== undefined ? { lengthAxis } : {}),
    };
}

function optionalOneOf<T extends string>(reader: FieldReader, fields: Fields, key: string, label: string, values: readonly T[]): T | undefined {
    return fields[key] === undefined ? undefined : reader.oneOf(fields, key, label, values);
}
